import { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';

import { imageVariantLink } from '@/common/media';
import { Avatar } from '@/components/Avatar/Avatar';
import { Input } from '@/components/Inputs';
import Modal from '@/components/Modal/Modal';
import type { Person } from '@/store/persons/person.dto';

import { getPersonFullName } from '../../people.helpers';
import { ROLE_COLORS } from '../../permissions.types';

import s from './AssignRoleModal.module.scss';

interface AssignRoleModalProps {
  open: boolean;
  /** The role being filled — the one whose page this was opened from. */
  roleName: string;
  /**
   * Everyone on the vessel who is not already in that role, with the role they hold now,
   * so the list can say what the move costs them.
   */
  candidates: readonly { person: Person; role: string }[];
  /** True while a previous pick is still in flight. */
  busy?: boolean;
  onClose: () => void;
  onAssign: (person: Person) => void;
}

/**
 * The other half of filling a role: the invite slot sends an email to someone new, this
 * picks someone who is already aboard. Assembled from Modal, Input and Avatar — the list
 * rows are this screen's, not a component, because nowhere else lists people to move.
 */
export function AssignRoleModal({
  open,
  roleName,
  candidates,
  busy = false,
  onClose,
  onAssign,
}: AssignRoleModalProps) {
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      ({ person, role }) =>
        getPersonFullName(person).toLowerCase().includes(q) || role.toLowerCase().includes(q),
    );
  }, [candidates, query]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Assign to ${roleName}`}>
      <div className={s.body}>
        <p className={s.intro}>
          Please pick someone already on this vessel. They keep their account and move out of their
          current role.
        </p>

        <Input
          placeholder="Search crew..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          startIcon={<FiSearch />}
        />

        {visible.length === 0 ? (
          <p className={s.empty}>
            {candidates.length === 0
              ? 'Everyone on this vessel is already in this role.'
              : 'No one matches. Please try widening your search.'}
          </p>
        ) : (
          <ul className={s.list}>
            {visible.map(({ person, role }) => (
              <li key={person.id}>
                <button
                  type="button"
                  className={s.row}
                  disabled={busy}
                  onClick={() => onAssign(person)}
                >
                  <Avatar
                    src={imageVariantLink(person.avatar, 36)}
                    firstName={person.person_info?.first_name ?? ''}
                    lastName={person.person_info?.last_name ?? ''}
                    size={36}
                  />
                  <span className={s.rowText}>
                    <span className={s.name}>{getPersonFullName(person)}</span>
                    {/* Blank when the person has no entry for this service yet — still a
                        valid pick, so say so rather than leaving the line empty. */}
                    <span className={s.role} style={{ color: ROLE_COLORS[role] ?? '#818cf8' }}>
                      {role || 'No role'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
